import { formFunctions } from '@/form/helpers/functions.js';

/**
 * What we hand the results screen when the submit produced no usable answer — axios gave
 * up, the connection dropped, or the server replied with something unreadable.
 *
 * It must be a NON-EMPTY object. MainApp only renders the results component while
 * processedApplication has keys, and the form unmounts the instant "completed" is
 * emitted, so an empty object leaves the user staring at a blank card instead of an
 * error. These are the same all-null fields the backend sends when it can't get an
 * answer out of CMI (CMI\API::processResponse, and the unknown-outcome branch of
 * MemberPortalController::createApplication), which lands on the "unable to match"
 * screen.
 */
const UNKNOWN_OUTCOME_APPLICATION = {
    leadUniqueId: null,
    buyers: null,
    redirectUrl: null,
    pixelUrl: null,
};

/**
 * Extracted submission/API logic from LcForm.
 * All state is accessed via refs from the component.
 *
 * @param {Object} deps
 * @param {import('vue').Ref} deps.app - ref
 * @param {Object} deps.applicationSettings - reactive (writable computed)
 * @param {Function} deps.updateApplication
 * @param {import('vue').Ref} deps.processedApplication - ref
 * @param {import('vue').Ref} deps.isSubmitting - ref
 * @param {Object} deps.loading - useLoadingAnimation return object
 */
export function useFormSubmission(deps) {

    function getFieldValue(fieldName) {
        return deps.app.value[fieldName] ?? null;
    }

    async function withLoadingAnimation(fn) {
        const appSettings = deps.applicationSettings;
        appSettings.loadingAnimationSeconds = -1;
        try {
            return await fn();
        } finally {
            appSettings.loadingAnimationSeconds = 0;
        }
    }

    async function executeEmailRegistrationCheck() {
        await withLoadingAnimation(async () => {
            let appSettings = deps.applicationSettings;
            let payload = {
                email: getFieldValue('email'),
                password: 'fakePassword'
            };

            let response = await formFunctions.apiLogin(payload);
            let validStatusCodes = [200, 201, 401];
            if (!!response.statusCode && validStatusCodes.includes(response.statusCode)) {
                appSettings.isUserRegistered = true;
                deps.app.value.is_user_registered = true;
            } else {
                appSettings.isUserRegistered = false;
                deps.app.value.is_user_registered = false;
            }
        });
    }

    async function executeEmailAuthentication() {
        await withLoadingAnimation(async () => {
            let appSettings = deps.applicationSettings;
            let payload = {
                email: getFieldValue('email'),
                password: getFieldValue('password')
            };

            let response = await formFunctions.apiLogin(payload);
            if (!!response.member) {
                appSettings.isUserAuthenticated = true;
                appSettings.isUserVerified = !!response.member.is_verified;
                deps.updateApplication(response.member);
            } else {
                appSettings.isUserAuthenticated = false;
                appSettings.isUserVerified = false;
            }
        });
    }

    async function executeCreateAccount() {
        return await withLoadingAnimation(async () => {
            let appSettings = deps.applicationSettings;
            let app = deps.app.value;

            if (
                app.wants_to_create_account && app.wants_to_create_account === 'yes' &&
                app.password && app.password.length > 0 &&
                app.password_confirmation && app.password_confirmation.length > 0 &&
                app.password === app.password_confirmation
                && (
                    !app.forcePhoneVerification ||
                    (app.verified_phone_number && app.verified_phone_number.length === 10)
                )
            ) {
                let payload = { ...app };

                let response = await formFunctions.apiCreateAccount(payload);
                if (!!response.member) {
                    appSettings.isUserAuthenticated = true;
                    deps.updateApplication(response.member);
                    return true;
                } else {
                    appSettings.isUserAuthenticated = false;
                    appSettings.userWantsVerification = false;
                    return false;
                }
            } else {
                appSettings.isUserAuthenticated = false;
                appSettings.userWantsVerification = false;
                return false;
            }
        });
    }

    async function executeSubmitApplication() {
        if (deps.isSubmitting.value) {
            return;
        }
        deps.isSubmitting.value = true;
        let appSettings = deps.applicationSettings;
        let app = deps.app.value;
        appSettings.loadingAnimationSeconds = -1;
        deps.loading.startRotatingMessages();

        let certificateUrl = null;
        let certificateSource = null;
        if (app?.product_name === 'life_insurance') {
            // ContactState get claimUrl
        } else if (app?.projectBrandCountry === 'US') {
            certificateSource = 'active-prospect';
            certificateUrl = await getActiveProspectCertificateUrl() ?? null;
        }

        let extraFields = {
            createPortalApp: !!appSettings.isUserAuthenticated,
            userAuthentication: {
                status: !!appSettings.isUserAuthenticated,
                email: getFieldValue('email'),
                password: getFieldValue('password'),
            },
            is_partial: false,
            prepop_token: app.token ?? null,
            lead_certificate_source: certificateSource,
            lead_certificate_url: certificateUrl,
        };

        // before submission lets load the animation if Flinks was completed
        if (app.completed_flinks && app.completed_flinks === 'yes') {
            await deps.loading.doLoadingAnimation(5);
            // doLoadingAnimation ends by taking the loading screen down, but the submit
            // hasn't even started yet: put it back up, or the last step reappears for the
            // whole request. The finally block below takes it down once the submit returns.
            appSettings.loadingAnimationSeconds = -1;
        }

        let payload = { ...app, ...extraFields };
        payload.lc_contact = '';
        payload.lc_referral = '';

        try {
            // Optional chaining throughout: on failure postWithStatusCheck resolves to the
            // string 'fail', and to undefined when the body has no status — reading
            // .application off undefined would throw, and the throw would skip both the
            // "completed" emit and the spinner reset, hanging the user on a spinner forever.
            let response = await formFunctions.apiSubmitApplication(payload);
            if (!!response?.application) {
                deps.updateApplication(response.application);
            }
            if (!!response?.processedApplication) {
                deps.processedApplication.value = response.processedApplication;
            } else {
                // No usable answer — see UNKNOWN_OUTCOME_APPLICATION. Never leave this
                // empty: the user gets a blank card rather than a screen.
                //
                // The submit may still have succeeded on the server after we stopped
                // listening, so if partial submits already gave us this lead's id, point
                // at the thank-you page for it rather than claiming there was no match.
                // That page asks CMI for the real outcome, including any buyers.
                const leadUniqueId = deps.app.value?.unique_id ?? null;
                deps.processedApplication.value = {
                    ...UNKNOWN_OUTCOME_APPLICATION,
                    leadUniqueId,
                    // `recovered=1` tells ThankYouController this visit is the lost-response
                    // fallback (the only entry allowed to complete the lender hop); every
                    // other /thank-you?unique_id link — SMS, email, /redirect fallback —
                    // must render the page as before.
                    thankYouUrl: leadUniqueId
                        ? `/thank-you?unique_id=${encodeURIComponent(leadUniqueId)}&recovered=1`
                        : null,
                };
            }
        } finally {
            deps.isSubmitting.value = false;
            deps.loading.stopRotatingMessages();
            // Inside the finally: anything that escaped above would otherwise leave the
            // loading animation running with no way out.
            appSettings.loadingAnimationSeconds = 0;
        }
    }

    function enablePartialRequestPerStep() {
        deps.applicationSettings.partialRequestOnEveryStep = true;
    }

    async function executeCreatePartialApp() {
        let appSettings = deps.applicationSettings;
        let app = deps.app.value;
        const isFirstRequest = appSettings.partialRequestsCount === 0;

        if (isFirstRequest) {
            appSettings.loadingAnimationSeconds = -1;
            deps.loading.startRotatingMessages();
        }

        let extraFields = {
            is_partial: true,
            prepop_token: app.token ?? null,
        };

        let payload = { ...app, ...extraFields };
        payload.lc_contact = '';
        payload.lc_referral = '';

        if (isFirstRequest) {
            await submitApplicationAndUpdate(payload);
        } else {
            submitApplicationAndUpdate(payload).catch(console.error);
        }

        if (isFirstRequest) {
            deps.loading.stopRotatingMessages();
            appSettings.loadingAnimationSeconds = 0;
        }
        appSettings.partialRequestsCount++;
    }

    async function submitApplicationAndUpdate(payload) {
        let response = await formFunctions.apiSubmitApplication(payload);
        if (!!response.processedApplication) {
            let app = deps.app.value;
            if (!app.hasOwnProperty('unique_id') || !app['unique_id']) {
                const fieldsToUpdate = {
                    unique_id: response.processedApplication.leadUniqueId ?? null
                };
                await deps.updateApplication(fieldsToUpdate);
            }
        }
    }

    async function getContactStateClaimUrl() {
        if (window.cscertify && typeof window.cscertify === 'function') {
            try {
                return await cscertify('send', { form_id: 'formContainer' });
            } catch (err) {
                console.error('Page script: There was an error', err);
                return null;
            }
        }
        console.error('cscertify is not available');
        return null;
    }

    async function getActiveProspectCertificateUrl() {
        const activeProspectForm = document.querySelector('form#active-prospect');
        if (activeProspectForm) {
            const certUrlInput = activeProspectForm.querySelector('[name="cmiTrustedFormCertUrl"]');
            return certUrlInput?.value || null;
        }
        return null;
    }

    return {
        getFieldValue,
        executeEmailRegistrationCheck,
        executeEmailAuthentication,
        executeCreateAccount,
        executeSubmitApplication,
        enablePartialRequestPerStep,
        executeCreatePartialApp,
        submitApplicationAndUpdate,
        getContactStateClaimUrl,
        getActiveProspectCertificateUrl,
    };
}
